import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true
  },
  global: {
    headers: {
      apikey: supabaseAnonKey
    }
  }
});

// Auth helper functions
export const signUp = async (email: string, password: string) => {
  try {
    // First sign up the user through Supabase Auth
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });
    
    if (error) {
      console.error('Auth signup error:', error);
      return { data, error };
    }
    
    // If signup was successful and we have a user
    if (data.user) {
      // Create a profile record
      const { error: profileError } = await supabase
        .from('profiles')
        .insert([
          {
            id: data.user.id,
            email: email,
            display_name: email.split('@')[0], // Default display name from email
            role: 'user' // Default role
          }
        ]);
      
      if (profileError) {
        console.error('Error creating profile:', profileError);
        return { data: null, error: { message: 'Failed to create profile', details: profileError } };
      }
    }
    
    return { data, error };
  } catch (err) {
    console.error('Signup error:', err);
    return { data: null, error: err };
  }
};

export const signIn = async (email: string, password: string) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  return { data, error };
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  return { error };
};

export const getCurrentUser = async () => {
  const { data } = await supabase.auth.getUser();
  return data?.user;
};

// DB helper functions
export const getUserProfile = async (userId: string) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();
  
  return { data, error };
};

export const updateUserProfile = async (userId: string, updates: any) => {
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', userId);
  
  return { data, error };
};

// Ideas
export const getIdeas = async (userId: string) => {
  const { data, error } = await supabase
    .from('ideas')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  
  return { data, error };
};

export const createIdea = async (idea: any) => {
  const { data, error } = await supabase
    .from('ideas')
    .insert([idea]);
  
  return { data, error };
};

export const updateIdea = async (id: string, updates: any) => {
  const { data, error } = await supabase
    .from('ideas')
    .update(updates)
    .eq('id', id);
  
  return { data, error };
};

export const deleteIdea = async (id: string) => {
  const { error } = await supabase
    .from('ideas')
    .delete()
    .eq('id', id);
  
  return { error };
};

// Campaigns
export const getCampaigns = async (userId: string) => {
  try {
    // Use the marketing_analyses table instead of the non-existent campaigns table
    const { data, error } = await supabase
      .from('marketing_analyses')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    
    // Format the data to match the expected campaigns format
    const formattedData = data?.map(item => ({
      id: item.id,
      user_id: item.user_id,
      name: item.title,
      subject: item.industry,
      status: 'active',
      recipients: item.competitors?.length || 0,
      created_at: item.created_at
    }));
    
    return { data: formattedData, error };
  } catch (error) {
    console.error('Error fetching campaigns:', error);
    return { data: [], error };
  }
};

export const createCampaign = async (campaign: any) => {
  const { data, error } = await supabase
    .from('campaigns')
    .insert([campaign]);
  
  return { data, error };
};

export const updateCampaign = async (id: string, updates: any) => {
  const { data, error } = await supabase
    .from('campaigns')
    .update(updates)
    .eq('id', id);
  
  return { data, error };
};

// Lead Scoring
export const getLeadScores = async (userId: string) => {
  const { data, error } = await supabase
    .from('lead_scores')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  
  return { data, error };
};

export const createLeadScore = async (leadScore: any) => {
  const { data, error } = await supabase
    .from('lead_scores')
    .insert([leadScore]);
  
  return { data, error };
};

// Proposals
export const getProposals = async (userId: string) => {
  const { data, error } = await supabase
    .from('proposals')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  
  return { data, error };
};

export const createProposal = async (proposal: any) => {
  const { data, error } = await supabase
    .from('proposals')
    .insert([proposal]);
  
  return { data, error };
};

export const updateProposal = async (id: string, updates: any) => {
  const { data, error } = await supabase
    .from('proposals')
    .update(updates)
    .eq('id', id);
  
  return { data, error };
};

// Marketing Analysis
export const getMarketingAnalyses = async (userId: string) => {
  const { data, error } = await supabase
    .from('marketing_analyses')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  
  return { data, error };
};

export const createMarketingAnalysis = async (analysis: any) => {
  const { data, error } = await supabase
    .from('marketing_analyses')
    .insert([analysis]);
  
  return { data, error };
};

// Strategies
export const getStrategies = async (userId: string) => {
  const { data, error } = await supabase
    .from('strategies')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  
  return { data, error };
};

export const createStrategy = async (strategy: any) => {
  const { data, error } = await supabase
    .from('strategies')
    .insert([strategy]);
  
  return { data, error };
};

export const updateStrategy = async (id: string, updates: any) => {
  const { data, error } = await supabase
    .from('strategies')
    .update(updates)
    .eq('id', id);
  
  return { data, error };
};

// KPIs
export const getKpis = async (userId: string) => {
  const { data, error } = await supabase
    .from('kpis')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  
  return { data, error };
};

export const createKpi = async (kpi: any) => {
  const { data, error } = await supabase
    .from('kpis')
    .insert([kpi]);
  
  return { data, error };
};

export const updateKpi = async (id: string, updates: any) => {
  const { data, error } = await supabase
    .from('kpis')
    .update(updates)
    .eq('id', id);
  
  return { data, error };
};

// NPS Scores
export const getNpsScores = async (userId: string) => {
  const { data, error } = await supabase
    .from('nps_scores')
    .select('*')
    .eq('user_id', userId)
    .order('date', { ascending: false });
  
  return { data, error };
};

// Feedback
export const getFeedback = async (userId: string) => {
  const { data, error } = await supabase
    .from('feedback')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  
  return { data, error };
};

export const createFeedback = async (feedback: any) => {
  const { data, error } = await supabase
    .from('feedback')
    .insert([feedback]);
  
  return { data, error };
};